// polyfill for Safari / IE 11
import { App, Directive, DirectiveBinding, VNode } from 'vue';

/**
 * The intersection observer directive is used to detect when the element
 * is visible is the viewport. It emits 'intersect-enter' when element becomes
 * visible in the viewport and 'intersect-leave' when element leaves the
 * viewport. Usage as below:
 *
 * v-intersection-observer="{ rootMargin: '<yourRootMarginValue>', threshold: '<yourThresholdValue>' }"
 *
 * To handle viewoport enter/leave events, declare following callbacks:
 *
 * @viewport-entered="<yourViewportEnteredHandler>"
 * @viewport-left="<yourViewportLeftHandler>"
 *
 * @type {import("vue").DirectiveOptions}
 */
export const IntersectionObserverDirective: Directive = {
  mounted: (el: HTMLElement, binding: DirectiveBinding, vnode: VNode) => {
    let observer: IntersectionObserver | undefined;

    function emit(eventName: string) {
      vnode.el?.dispatchEvent(new CustomEvent(eventName));
    }

    function handleIntersect(entries: IntersectionObserverEntry[]) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          emit('viewport-entered');

          if (binding.value.once) {
            observer?.disconnect();
            observer = undefined;
          }
        } else {
          emit('viewport-left');
        }
      });
    }

    function createObserver() {
      const options: IntersectionObserverInit = {
        root: null, // defaults to the browser viewport if not specified or if null
        threshold: binding.value.threshold || 0, // the degree of intersection between the target element and its root (0 - 1)
        // threshold of 1.0 means that when 100% of the target is visible within
        // the element specified by the root option, the callback is invoked
        rootMargin: binding.value.rootMargin || '0px',
      };

      observer = new IntersectionObserver(handleIntersect, options);
      observer.observe(el); // target element to watch
    }

    if (window.IntersectionObserver) {
      createObserver();
    }
  },
};

export function registerDirective(Vue: App) {
  Vue.directive('intersection-observer', IntersectionObserverDirective);
}
